import type { LayoutOptions } from '@base/shared';
import { Bell, ChevronDown, ChevronRight, Command, Search } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '../components/ui.tsx';
import { SampleIcon, type SampleArea, type SampleDomain } from './samples/index.ts';

// A moldura de cada layout na prévia do estúdio (ADR 0133, parte 3), com o menu do exemplo de
// negócio: áreas, telas da área, contadores, busca, avisos e a pessoa. Usa as mesmas classes da
// moldura de verdade (.ui-sidebar, .ui-nav-active), então o acabamento vale igual.

const main = (d: SampleDomain) => d.areas.find((a) => a.id === d.mainArea)!;

function Brand({ d, compact = false }: { d: SampleDomain; compact?: boolean }) {
  // Com o logo do sistema, como a lateral de verdade: o logo em cima e o nome embaixo.
  if (d.logo)
    return compact ? (
      <img src={d.logo} alt="" className="h-8 w-8 shrink-0 object-contain object-left" />
    ) : (
      <span className="flex min-w-0 flex-col gap-1">
        <img src={d.logo} alt="" className="h-8 max-w-[10rem] self-start object-contain" />
        <span className="truncate text-[11px] text-sidebar-muted">{d.brand[0]}</span>
      </span>
    );
  return (
    <span className="flex min-w-0 items-center gap-2.5">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-accent text-xs font-bold text-on-accent">
        {d.brand[2]}
      </span>
      {!compact && (
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-semibold">{d.brand[0]}</span>
          <span className="truncate text-[11px] text-sidebar-muted">{d.brand[1]}</span>
        </span>
      )}
    </span>
  );
}

function Count({ n }: { n?: number }) {
  return n ? (
    <span className="ml-auto rounded-full bg-sidebar-hover px-1.5 text-[11px] tabular-nums">
      {n}
    </span>
  ) : null;
}

/** Um item do menu na lateral; a área principal abre as telas dela pelo jeito do submenu. */
function NavItem({ a, d, o }: { a: SampleArea; d: SampleDomain; o: LayoutOptions }) {
  const isMain = a.id === d.mainArea;
  const open = isMain && a.children && o.submenu !== 'flyout';
  return (
    <div className="flex flex-col gap-0.5">
      {a.cap && <span className="ui-cap mt-3 px-2.5 text-[11px] text-sidebar-muted">{a.cap}</span>}
      <span
        className={`flex min-h-9 items-center gap-2.5 rounded-md px-2.5 text-sm ${isMain && !a.children ? 'ui-nav-active' : isMain ? 'font-medium' : 'text-sidebar-text/90'}`}
      >
        {o.navLabels === 'icons' && <SampleIcon name={a.icon} aria-hidden />}
        {a.label}
        <Count n={a.count} />
        {a.children &&
          (open ? (
            <ChevronDown size={14} aria-hidden className="text-sidebar-muted" />
          ) : (
            <ChevronRight size={14} aria-hidden className="text-sidebar-muted" />
          ))}
      </span>
      {open &&
        a.children!.map((c) => (
          <span
            key={c}
            className={`flex min-h-8 items-center rounded-md pr-2.5 text-[13px] ${o.navLabels === 'icons' ? 'pl-9' : 'pl-5'} ${c === d.mainChild ? 'ui-nav-active font-medium' : 'text-sidebar-muted'}`}
          >
            {c}
          </span>
        ))}
    </div>
  );
}

export function Sidebar({ d, o }: { d: SampleDomain; o: LayoutOptions }) {
  return (
    <nav className="ui-sidebar flex w-60 shrink-0 flex-col gap-1 px-3 py-4">
      <span className="mb-3 px-1">
        <Brand d={d} />
      </span>
      {d.areas.map((a) => (
        <NavItem key={a.id} a={a} d={d} o={o} />
      ))}
    </nav>
  );
}

export function Rail({ d }: { d: SampleDomain }) {
  return (
    <nav className="ui-sidebar flex w-16 shrink-0 flex-col items-center gap-1.5 py-4">
      <span className="mb-2">
        <Brand d={d} compact />
      </span>
      {d.areas.map((a) => {
        return (
          <span
            key={a.id}
            className={`flex w-12 flex-col items-center gap-0.5 rounded-md py-1.5 text-[10px] ${a.id === d.mainArea ? 'ui-nav-active' : 'text-sidebar-muted'}`}
          >
            <SampleIcon name={a.icon} aria-hidden />
            <span className="max-w-full truncate">{a.label}</span>
          </span>
        );
      })}
    </nav>
  );
}

/** A coluna das telas da área (Trilho e coluna, Mestre e detalhe). */
export function Column({ d }: { d: SampleDomain }) {
  const a = main(d);
  return (
    <div className="flex w-52 shrink-0 flex-col gap-0.5 border-r border-rule bg-sheet px-3 py-4">
      <span className="mb-2 px-2.5 text-sm font-semibold">{a.label}</span>
      {(a.children ?? []).map((c) => (
        <span
          key={c}
          className={`flex min-h-8 items-center rounded-md px-2.5 text-[13px] ${c === d.mainChild ? 'bg-brand-soft font-medium text-brand-text' : 'text-muted'}`}
        >
          {c}
        </span>
      ))}
    </div>
  );
}

/** A barra de cima: busca geral, avisos e a pessoa; no Topo com faixa, também as áreas. */
export function TopBar({
  d,
  areas = false,
  dark = false,
}: {
  d: SampleDomain;
  areas?: boolean;
  dark?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={`flex h-14 shrink-0 items-center gap-4 px-4 ${dark ? 'ui-sidebar' : 'border-b border-rule bg-sheet'}`}
    >
      {(areas || dark) && <Brand d={d} />}
      {areas && (
        <span className="flex items-center gap-1">
          {d.areas
            .filter((a) => !a.cap || a.id === d.mainArea || a.cap !== 'Gestão')
            .slice(0, 6)
            .map((a) => (
              <span
                key={a.id}
                className={`rounded-md px-2.5 py-1.5 text-sm ${a.id === d.mainArea ? 'ui-nav-active' : 'text-sidebar-muted'}`}
              >
                {a.label}
              </span>
            ))}
        </span>
      )}
      <span
        className={`ui-control ui-input ml-auto flex w-72 items-center gap-2 border px-3 text-sm text-muted ${areas || dark ? 'max-w-56' : ''}`}
      >
        <Search size={15} aria-hidden />
        {t('style.demo.searchAll')}
      </span>
      <Bell size={18} aria-hidden className="text-muted" />
      <Avatar name={d.user[0]} size={30} />
    </div>
  );
}

/** As telas da área numa faixa embaixo do topo (Topo com faixa). */
export function SubStrip({ d }: { d: SampleDomain }) {
  const a = main(d);
  return (
    <div className="flex shrink-0 gap-1 border-b border-rule bg-sheet px-4 py-1.5">
      {(a.children ?? []).map((c) => (
        <span
          key={c}
          className={`rounded-md px-2.5 py-1 text-[13px] ${c === d.mainChild ? 'bg-brand-soft font-medium text-brand-text' : 'text-muted'}`}
        >
          {c}
        </span>
      ))}
    </div>
  );
}

export function WorkTabs({ d }: { d: SampleDomain }) {
  const tabs = [d.mainChild, d.rows[d.mainIdx]!.id, ...(main(d).children ?? []).slice(1, 3)];
  return (
    <div className="flex shrink-0 items-end gap-1 border-b border-rule bg-paper px-3 pt-1.5">
      {tabs.map((tab, i) => (
        <span
          key={tab}
          className={`rounded-t-md px-3 py-2 text-[13px] ${i === 0 ? 'border border-b-0 border-rule bg-sheet font-medium' : 'text-muted'}`}
        >
          {tab}
        </span>
      ))}
    </div>
  );
}

export function StatusBar({ d }: { d: SampleDomain }) {
  const { t } = useTranslation();
  return (
    <div className="flex h-7 shrink-0 items-center gap-3 border-t border-rule bg-sheet px-3 text-[11px] text-muted">
      <span className="size-1.5 rounded-full bg-ok" aria-hidden />
      {t('nav.connected')} · {d.brand[0]} · {d.user[0]}
    </div>
  );
}

export function Tiles({ d, icons }: { d: SampleDomain; icons: boolean }) {
  return (
    <div className="grid content-start gap-3 p-6 sm:grid-cols-3">
      {d.areas.map((a) => {
        return (
          <span
            key={a.id}
            className={`ui-panel flex rounded-lg bg-sheet p-4 ${icons ? 'flex-col items-center gap-2 text-center' : 'items-start gap-3'}`}
          >
            <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-brand-soft text-brand-text">
              <SampleIcon name={a.icon} size={20} aria-hidden />
            </span>
            <span className="flex flex-col gap-0.5">
              <span className="text-sm font-medium">{a.label}</span>
              {!icons && a.children && (
                <span className="text-xs text-muted">{a.children.join(' · ')}</span>
              )}
            </span>
          </span>
        );
      })}
    </div>
  );
}

export function FocusBar({ d, topLinks }: { d: SampleDomain; topLinks: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="flex h-14 shrink-0 items-center gap-4 border-b border-rule bg-sheet px-5">
      <Brand d={d} />
      <span className="ui-control ui-input mx-auto flex w-96 items-center gap-2 border px-3 text-sm text-muted">
        <Command size={14} aria-hidden />
        {t('nav.command')}
        <kbd className="ml-auto rounded border border-rule px-1.5 font-mono text-[11px]">
          Ctrl K
        </kbd>
      </span>
      {topLinks &&
        (main(d).children ?? []).slice(0, 3).map((c) => (
          <span key={c} className="text-sm text-muted">
            {c}
          </span>
        ))}
    </div>
  );
}

export function SheetTabs({ d }: { d: SampleDomain }) {
  return (
    <div className="ui-sidebar flex shrink-0 items-end gap-1 px-3 pt-2">
      <span className="mr-2 mb-2">
        <Brand d={d} compact />
      </span>
      {d.areas.map((a) => (
        <span
          key={a.id}
          className={`rounded-t-md px-3 py-2 text-[13px] ${a.id === d.mainArea ? 'bg-sheet font-medium text-ink' : 'text-sidebar-muted'}`}
        >
          {a.label}
        </span>
      ))}
    </div>
  );
}

export function Ribbon({ d, compact }: { d: SampleDomain; compact: boolean }) {
  const { t } = useTranslation();
  const items: [ReactNode, boolean][] = [
    [d.list.newL, true],
    [t('style.sample.secondary'), false],
    [t('style.demo.copy'), false],
    [t('style.demo.remove'), false],
  ];
  return (
    <div
      className={`flex shrink-0 items-center gap-2 border-b border-rule bg-sheet px-3 ${compact ? 'py-1' : 'py-2'}`}
    >
      {items.map(([label, primary], i) => (
        <span
          key={i}
          className={`ui-btn ui-control inline-flex items-center px-3 text-xs ${primary ? 'ui-btn-primary' : 'ui-button-secondary'}`}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

/** O painel do item escolhido (Três colunas). */
export function Inspector({ d }: { d: SampleDomain }) {
  const { t } = useTranslation();
  const r = d.rows[d.mainIdx]!;
  return (
    <aside className="flex w-72 shrink-0 flex-col gap-3 border-l border-rule bg-sheet p-4 text-sm">
      <span className="ui-cap text-[11px] text-muted">{t('nav.inspector')}</span>
      <span className="font-mono text-xs text-brand-text">{r.id}</span>
      <span className="text-base font-semibold">{r.title}</span>
      <span className="text-muted">{r.c3}</span>
      {d.rec.summary.map(([k, v]) => (
        <span key={k} className="flex justify-between gap-2 border-t border-rule pt-2">
          <span className="text-muted">{k}</span>
          <span className="tabular-nums">{v}</span>
        </span>
      ))}
    </aside>
  );
}

/** A lista na coluna do Mestre e detalhe. */
export function ListColumn({ d, width, item }: { d: SampleDomain; width: string; item: string }) {
  const w = { narrow: 'w-64', medium: 'w-80', wide: 'w-96' }[width] ?? 'w-80';
  return (
    <div className={`${w} flex shrink-0 flex-col border-r border-rule bg-sheet`}>
      {d.rows.map((r, i) => (
        <span
          key={r.id}
          className={`flex flex-col gap-0.5 border-b border-rule px-4 ${item === 'row' ? 'py-2' : 'py-3'} ${i === d.mainIdx ? 'bg-brand-soft shadow-[inset_3px_0_0_var(--accent)]' : ''}`}
        >
          <span className="flex justify-between text-xs text-muted">
            <span className="font-mono">{r.id}</span>
            {item !== 'row' && r.d1}
          </span>
          <span className="truncate text-sm font-medium">{r.title}</span>
        </span>
      ))}
    </div>
  );
}
